// ============================================================================
// REQUEST ENDPOINTS - Digital Approval System
// ============================================================================
// Endpoint definitions for Request module

import { buildEndpoint, buildEndpointWithParams } from './base.endpoints';
import type { ID } from '../types';

/**
 * Base path for request endpoints
 */
const BASE_PATH = '/das/requests';

/**
 * Request Endpoints
 */
export const requestEndpoints = {
  /**
   * Get all requests with pagination and filtering
   * GET /das/requests
   */
  list: () => buildEndpoint(BASE_PATH),

  /**
   * Get request by ID
   * GET /das/requests/:id
   */
  getById: (id: ID) => buildEndpointWithParams(`${BASE_PATH}/:id`, { id }),

  /**
   * Get complete request details including approvers, attachments, and related tickets
   * GET /das/requests/:id/details
   */
  getDetails: (id: ID) => buildEndpointWithParams(`${BASE_PATH}/:id/details`, { id }),

  /**
   * Get request by request number
   * GET /das/requests/number/:requestNumber
   */
  getByNumber: (requestNumber: string) =>
    buildEndpointWithParams(`${BASE_PATH}/number/:requestNumber`, { requestNumber }),

  /**
   * Lookup requests whose request number OR external reference number matches the term.
   * GET /das/requests/lookup?term=...
   */
  lookup: (term: string) =>
    `${buildEndpoint(`${BASE_PATH}/lookup`)}?term=${encodeURIComponent(term)}`,

  /**
   * Update external reference number on a request (das_admin only)
   * PATCH /das/requests/:id/external-reference
   */
  updateExternalReference: (id: ID) =>
    buildEndpointWithParams(`${BASE_PATH}/:id/external-reference`, { id }),

  /**
   * Get requests by status
   * GET /das/requests/status/:status
   */
  getByStatus: (status: string) =>
    buildEndpointWithParams(`${BASE_PATH}/status/:status`, { status }),

  /**
   * Get current user's requests
   * GET /das/requests/my-requests
   */
  getMyRequests: () => buildEndpoint(`${BASE_PATH}/my-requests`),

  /**
   * Get requests by request type
   * GET /das/requests/request-type/:requestTypeId
   */
  getByRequestType: (requestTypeId: ID) =>
    buildEndpointWithParams(`${BASE_PATH}/request-type/:requestTypeId`, { requestTypeId }),

  /**
   * Create new request
   * POST /das/requests
   */
  create: () => buildEndpoint(BASE_PATH),

  /**
   * Update existing request
   * PUT /das/requests/:id
   */
  update: (id: ID) => buildEndpointWithParams(`${BASE_PATH}/:id`, { id }),

  /**
   * Submit draft request
   * POST /das/requests/:id/submit
   */
  submit: (id: ID) => buildEndpointWithParams(`${BASE_PATH}/:id/submit`, { id }),

  /**
   * Update request status
   * PATCH /das/requests/:id/status
   */
  updateStatus: (id: ID) => buildEndpointWithParams(`${BASE_PATH}/:id/status`, { id }),

  /**
   * Delete request
   * DELETE /das/requests/:id
   */
  delete: (id: ID) => buildEndpointWithParams(`${BASE_PATH}/:id`, { id }),

  /**
   * Validate request number uniqueness
   * GET /das/requests/validate/number/:requestNumber
   */
  validateNumber: (requestNumber: string, excludeId?: ID) => {
    const endpoint = buildEndpointWithParams(
      `${BASE_PATH}/validate/number/:requestNumber`,
      { requestNumber }
    );
    return excludeId ? `${endpoint}?excludeId=${excludeId}` : endpoint;
  },
};

export default requestEndpoints;
